import omniclone from 'omniclone';
import _ from 'lodash';

import {DATATABLE_SAVE_DATA} from '~/frontend/src/constants';

import {__} from '~/shared/src/utilities/messageCatalog';
import {SaveDataResult} from '~/shared/src/utilities/modules/data';
import {ItemsAndReferencesCollection} from '~/shared/src/utilities/dataUtils';

import {
    StoreState,
    AppDispatch,
    Thunk
} from '~/frontend/src/store';
import {
    isDataModified,
    isDataModifiedRecursive
} from '~/frontend/src/utilities/dataUtils';
import {
    MsgParams,
    setMessage
} from '~/frontend/src/actions/intercom';
import dataSaveData from '~/frontend/src/actions/data/saveData';
import {
    clearElusiveData,
    setElusiveData,
    unsetElusiveData
} from '~/frontend/src/actions/elusiveData';
import {getPanelIndex} from '~/frontend/src/components/base/page/PageTabPanel/utilities';
import setParams from '~/frontend/src/actions/panel/common/setParams';
import unsetParam from '~/frontend/src/actions/panel/common/unsetParam';
import deferedInputValueUpdate from '~/frontend/src/actions/panel/common/deferedInputValueUpdate';

type Parameters = {
    [ key : string ] : any;
};

/**
 * @memberOf! Actions
 *
 * @param {string} panelKey
 * @param {Schema} schema
 * @param {number} rootItemId
 * @param {string} requestModule
 * @param {string} requestCommand
 * @param {Parameters | undefined} [requestParams=undefined]
 * @param {MsgParams | undefined} [successMsgParams=undefined]
 * @param {MsgParams | undefined} [nothingToSaveMsgParams=undefined]
 * @param {MsgParams | undefined} [errorMsgParams=undefined]
 *
 */
const saveData = ( panelKey : string, schema : Schema, rootItemId : number, requestModule : string = 'data', requestCommand : string = 'save', requestParams : Parameters | undefined = undefined, successMsgParams : MsgParams | undefined = undefined, nothingToSaveMsgParams : MsgParams | undefined = undefined, errorMsgParams : MsgParams | undefined = undefined ) : Thunk<Promise<SaveDataResult | false>> => async ( dispatch : AppDispatch, getState : () => StoreState ) : Promise<SaveDataResult | false> => {
    dispatch( {
        type: DATATABLE_SAVE_DATA,
        panelKey,
        schema
    } );

    await dispatch( deferedInputValueUpdate( panelKey, panelKey, schema ) );

    const requestUid   = 'saveData',
          dataModified = rootItemId
              ? isDataModifiedRecursive( panelKey, schema, rootItemId, false )
              : isDataModified( panelKey, schema );

    let retVal : SaveDataResult | false = false;

    // nothing to save
    if ( !Object.keys( requestParams ?? {} ).length && !dataModified ) {
        if ( !nothingToSaveMsgParams ) {
            nothingToSaveMsgParams = [
                panelKey,
                'toast',
                'nothingToSave',
                {
                    severity: 'info',
                    summary : __( 'YN.base.treeView.save.nothingToSave.title' ),
                    detail  : __( 'YN.base.treeView.save.nothingToSave.text' )
                }
            ];
        }

        dispatch( setMessage( ...nothingToSaveMsgParams ) );
    }

    // save request running
    else if ( getState().xhr?.[ panelKey ]?.[ requestUid ] ) {
        dispatch( setMessage(
            panelKey,
            'toast',
            'saveRunning',
            {
                severity: 'info',
                summary : __( 'YN.base.treeView.save.running.title' ),
                detail  : __( 'YN.base.treeView.save.running.text' )
            }
        ) );
    }

    // save
    else {

        // get current panelProps of TreeView
        const
            panelIndex                                      = getPanelIndex( panelKey ),
            panelProps                                      = panelIndex !== undefined ? omniclone( getState().panelManager?.openPanels[ panelIndex ]?.panelProps, {
                invokeConstructors: false,
                setPrototype      : true,
                copyGettersSetters: true
            } ) : undefined,
            modifiedTreeData : ItemsAndReferencesCollection = { items: [], references: [] },
            treeProps                                       = panelProps.subPanelProps?.[ 'tree' ];

        // clear dirty
        panelProps.dirtyNodeIds = undefined;

        // get all modified items and references represented by a tree node (DynamicNode)
        if ( treeProps?.flatNodeMap ) {
            for ( const nodeId in treeProps.flatNodeMap ) {
                const
                    payloadId          = treeProps.flatNodeMap[ nodeId ].data.payloadId,
                    payloadItem        = global.data[ panelKey ].items[ payloadId ],
                    payloadReferenceId = treeProps.flatNodeMap[ nodeId ].data.payloadReferenceId,
                    payloadReference   = global.data[ panelKey ].references[ payloadReferenceId ];

                if ( payloadItem?.isModified() && !modifiedTreeData.items.includes( payloadItem ) ) {
                    modifiedTreeData.items.push( payloadItem );
                }
                if ( payloadReference?.isModified() && !modifiedTreeData.references.includes( payloadReference ) ) {
                    modifiedTreeData.references.push( payloadReference );
                }
            }
        }

        const subPanelKeys = ( Object.keys( getState().elusiveData ?? {} ) as string[] ).filter( key => key.match( `^${panelKey}[_-](.*)$` ) ),
              result       = await dispatch( dataSaveData(
                  panelKey,
                  panelKey,
                  requestUid,
                  schema,
                  `${requestModule}/${requestCommand}`,
                  true,
                  [ panelKey + '_treeview', panelKey + '_DataObjects' ].concat( subPanelKeys ),
                  rootItemId,
                  modifiedTreeData,
                  requestParams
              ) );

        retVal = result;

        // save success
        if ( result.success ) {
            const elusiveData    = getState().elusiveData?.[ panelKey ],
                  newElusiveData = {
                      ...elusiveData,
                      lastSaveRequestParameters: JSON.stringify( requestParams ),
                      lastSaveTimestamp        : Date.now(),
                      newItemIdMapping         : Object.keys( result.data.itemMetaDataMapping ).reduce(
                          ( innerRetVal, oldId ) => {
                              innerRetVal[ oldId ] = result.data.itemMetaDataMapping[ oldId ].id;

                              return innerRetVal;
                          },
                          {} as { [ oldId : number ] : number }
                      )
                  };

            // clear dirtyNodeIds
            dispatch( unsetParam( panelKey, 'dirtyNodeIds' ) );

            // if itemIdMapping has changed, update TreeView / ViewDesigner panel itself
            if ( result.data.itemMetaDataMapping[ rootItemId ] ) {
                rootItemId = result.data.itemMetaDataMapping[ rootItemId ]?.id;

                newElusiveData.metaData = {
                    ...newElusiveData?.metaData,
                    newItemId: rootItemId
                };

                if ( panelProps && panelProps.rootItemId !== rootItemId ) {
                    panelProps.rootItemId = rootItemId;
                }

                newElusiveData.data!.rootItemId = rootItemId;
            }

            dispatch( setElusiveData( panelKey, newElusiveData ) );

            const subPanelKeysToHandle = _.uniq( [ panelKey + '_treeview', panelKey + '_DataObjects', panelKey + '_tree' ].concat( subPanelKeys ) );

            // if elusive data from subpanels (e.g. Forms) has changed, update elusiveData for these subpanels
            for ( const subPanelKey of subPanelKeysToHandle ) {
                const subPanelElusiveData = getState().elusiveData?.[ subPanelKey ],
                      // TODO: check why subPanelProps uses the short form and subPanelErrors not. Unify it?!
                      shortSubPanelKey    = subPanelKey.replace( panelKey + '_', '' );

                if ( panelProps?.subPanelProps[ shortSubPanelKey ] ) {
                    const selectedPayloadId          = panelProps.subPanelProps[ shortSubPanelKey ].selectedPayloadId ?? 0,
                          selectedPayloadReferenceId = panelProps.subPanelProps[ shortSubPanelKey ].selectedPayloadReferenceId ?? 0;

                    // update selectedPayloadId the corresponding item was new and has been saved
                    if ( selectedPayloadId && result.data.itemMetaDataMapping[ selectedPayloadId ] ) {
                        panelProps.subPanelProps[ shortSubPanelKey ].selectedPayloadId = result.data.itemMetaDataMapping[ selectedPayloadId ].id;
                    }
                    if ( selectedPayloadReferenceId && result.data.referenceMetaDataMapping[ selectedPayloadReferenceId ] ) {
                        panelProps.subPanelProps[ shortSubPanelKey ].selectedPayloadReferenceId = result.data.referenceMetaDataMapping[ selectedPayloadReferenceId ].id;
                    }
                }

                // handle new items saved (item id was negative, and we received a itemId for it from database)
                if ( ( subPanelElusiveData?.data?.rootItemId ?? 0 ) < 0 && result.data.itemMetaDataMapping[ subPanelElusiveData?.data?.rootItemId ] ) {
                    const newRootItemId = result.data.itemMetaDataMapping[ subPanelElusiveData?.data?.rootItemId ].id;
                    const regEx = new RegExp( `${subPanelElusiveData!.data!.rootItemId}$` );
                    const newSubPanelKey      = subPanelKey.replace( regEx, newRootItemId + '' ),
                          newShortSubPanelKey = newSubPanelKey.replace( panelKey + '_', '' );

                    let nodeId : string | undefined;

                    // update nodeIdToPanelKeyMap
                    if ( panelProps?.nodeIdToPanelKeyMap && ( nodeId = _.findKey( panelProps.nodeIdToPanelKeyMap, pk => pk === subPanelKey ) ) !== undefined ) {
                        panelProps.nodeIdToPanelKeyMap[ nodeId ] = newSubPanelKey;
                    }

                    if ( subPanelKey !== newSubPanelKey ) {
                        // update subPanelProps
                        if ( panelProps?.subPanelProps?.[ shortSubPanelKey ] ) {
                            panelProps.subPanelProps[ newShortSubPanelKey ] = panelProps.subPanelProps[ shortSubPanelKey ];
                            delete panelProps.subPanelProps[ shortSubPanelKey ];
                        }

                        // update subPanelErrors
                        if ( panelProps?.subPanelErrors?.[ subPanelKey ] ) {
                            panelProps.subPanelErrors[ newSubPanelKey ] = panelProps.subPanelErrors[ subPanelKey ];
                            delete panelProps.subPanelErrors[ subPanelKey ];
                        }
                    }

                    dispatch( clearElusiveData( subPanelKey ) );

                    dispatch( setElusiveData( newSubPanelKey, {
                        ...subPanelElusiveData,
                        data                  : { ...subPanelElusiveData?.data, rootItemId: newRootItemId },
                        metaData              : { ...subPanelElusiveData?.metaData },
                        dirty                 : false,
                        isDirty               : false,
                        errorData             : undefined,
                        newItemIdMapping      : Object.keys( result.data.itemMetaDataMapping ).reduce(
                            ( innerRetVal, oldId ) => {
                                innerRetVal[ oldId ] = result.data.itemMetaDataMapping[ oldId ].id;

                                return innerRetVal;
                            },
                            {} as { [ oldId : number ] : number }
                        )
                    } ) );
                }
                else {
                    dispatch( setElusiveData( subPanelKey, {
                        ...subPanelElusiveData,
                        dirty                 : false,
                        isDirty               : false,
                        errorData             : undefined
                    } ) );
                }
            }

            // if any panelProps gets changed here, update them in store
            if ( panelProps ) {
                dispatch( setParams( panelKey, panelProps ) );
            }

            if ( !successMsgParams ) {
                successMsgParams = [
                    panelKey,
                    'toast',
                    'saveSuccess',
                    {
                        severity: 'success',
                        summary : __( 'YN.base.treeView.save.saveSuccess.title' ),
                        detail  : __( 'YN.base.treeView.save.saveSuccess.text' )
                    }
                ];
            }

            dispatch( setMessage( ...successMsgParams ) );

            // clear lastParams to trigger reload
            dispatch( unsetElusiveData( panelKey, [ 'lastParams' ] ) );
        }

        // save error
        else {
            if ( !errorMsgParams ) {
                errorMsgParams = [
                    panelKey,
                    'toast',
                    'saveError',
                    {
                        severity: 'error',
                        summary : __( 'YN.base.treeView.save.saveError.title' ),
                        detail  : __( 'YN.base.treeView.save.saveError.text' )
                    }
                ];
            }

            dispatch( setMessage( ...errorMsgParams ) );
        }
    }

    return retVal;
}
export type SaveData = typeof saveData;

export default saveData;
